/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
/* global ns_ */
// @flow

import { isEmpty } from 'lodash';
import { isLiveLinearSourceType } from 'Common/models/franchise';
import type { Store } from 'redux';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import BaseCastAnalytics from './BaseCastAnalytics';
import {
  APP_NAME,
  COMSCORE_PARTNER_ID,
  MILLIS_IN_SECOND,
  SECONDS_IN_MINUTE,
  AD_BREAK_TYPE_PREROLL,
  AD_BREAK_TYPE_MIDROLL,
  AD_BREAK_TYPE_POSTROLL,
  ADS_DELIVERY_SERVICE,
} from './AnalyticsConstants';

declare var ns_: Object;

const C3_VALUE = 'DANY';
const COMSCORE_NULL_STRING = '*null';
const TAG = 'COMSCORE[CAST]';

class ComscoreCastAnalytics extends BaseCastAnalytics {
  _sa: Object;
  onDemandAdsInfo: Object;
  contentMetadata: Object;
  previousPlayerState: string;
  enableComscoreDebug: boolean;

  constructor(playerManager: Object, store: Store<State, BaseAction | ThunkAction, Dispatch>) {
    super(playerManager, store);
    this._sa = new ns_.StreamingAnalytics();
    ns_.PlatformApi.setPlatformAPI(ns_.PlatformApi.PlatformApis.Chromecast);
    this.onDemandAdsInfo = {
      breakClipCurrentTimeSec: 0,
      breakClipDurationSec: 0,
      breaks: {},
      breakClips: {},
      isOndemandAdPlaying: false,
    };
    this.previousPlayerState = 'IDLE';
    this.contentMetadata = {};
    this.enableComscoreDebug = false; // set to 'true' if you want to see Comscore requests in console
    this.emptyValue = COMSCORE_NULL_STRING;
  }

  get enabled() {
    return this.customAttributes?.comscore_enabled;
  }

  onCafEvent(event: Object) {
    this.logAnalytics(TAG, 'Player event: ', event.type, event);
    switch (event.type) {
      case 'REQUEST_LOAD': {
        const { requestData } = event;
        if (this.isLiveLinearAdStart(requestData)) {
          this.onPlaybackFinished();
        }
        event.handleInSuper = true;
        break;
      }
      case 'PLAYER_LOADING':
        this.logAnalytics(TAG, 'analyticsData = ', this.analyticsData);
        if (!this.isInitialized) {
          this._configureAndStartComscore();
          this.isInitialized = true;
        }
        if (this.isLiveLinearAdStart(event)) {
          this._loadLiveLinearAdMetadata(event);
        } else if (this.isLiveLinearAdProceedPlaying(event)) {
          this.onPlaybackFinished();
          this._loadLiveLinearAdMetadata(event);
        } else if (this.isLiveLinearAdEnd(event)) {
          this.onPlaybackFinished();
        } else {
          this.logAnalytics(TAG, ' >>>>> ', 'createPlaybackSession()');
          this._sa.createPlaybackSession();
          if (isLiveLinearSourceType(this.sourceType)) {
            this.checkQvtAndStart(() => {
              this._prepareAndSetContentInfo();
            });
          } else {
            this._prepareAndSetContentInfo();
          }
        }
        break;
      case 'BUFFERING': {
        if (!this._isOdOrLlAdPlaying()) {
          if (event.isBuffering) {
            this.logAnalytics(TAG, ' >>>>> ', 'isBuffering = true');
            this.isBuffering = true;
            this.logAnalytics(TAG, ' >>>>> ', `startFromPosition(${this._getPlaybackPosition()})`);
            this._sa.startFromPosition(this._getPlaybackPosition());
            this.logAnalytics(TAG, ' >>>>> ', 'notifyBufferStart()');
            this._sa.notifyBufferStart();
          } else {
            this.logAnalytics(TAG, ' >>>>> ', 'isBuffering = false');
            this.isBuffering = false;
          }
        }
        break;
      }
      case 'PLAYING':
        if (!this._isOdOrLlAdPlaying()) {
          const position = this._getPlaybackPosition();
          // Log notifyBufferStop() if bufferring occured for content
          this.logAnalytics(TAG, ' >>>>> ', 'isBuffering = ', this.isBuffering);
          if (this.isBuffering) {
            this.logAnalytics(TAG, ' >>>>> ', `startFromPosition(${position})`);
            this._sa.startFromPosition(position);
            this.logAnalytics(TAG, ' >>>>> ', 'notifyBufferStop()');
            this._sa.notifyBufferStop();
          }
          // set content metadata and notify play
          this.logAnalytics(TAG, ' >>>>> ', 'Load content metadata');
          this._setMetadata(this.contentMetadata);
          this.logAnalytics(TAG, ' >>>>> ', `startFromPosition(${position})`);
          this._sa.startFromPosition(position);
          this._sa.notifyPlay();
          this.logAnalytics(TAG, ' >>>>> ', 'notifyPlay()');
        }
        break;
      case 'REQUEST_PLAY':
        if (this._isOdOrLlAdPlaying()) {
          this.logAnalytics(TAG, ' >>>>> ', 'notifyPlay()');
          this._sa.notifyPlay();
        }
        break;
      case 'REQUEST_PAUSE':
        this.logAnalytics(TAG, ' >>>>> ', 'notifyPause()');
        this._sa.notifyPause();
        break;
      case 'REQUEST_SEEK':
        this.logAnalytics(TAG, ' >>>>> ', 'notifySeekStart()');
        this._sa.notifySeekStart();
        break;
      case 'MEDIA_STATUS':
        this._playerStateChanged(event);
        break;
      case 'RATE_CHANGE':
        this.logAnalytics(
          TAG,
          ' >>>>> ',
          'notifyChangePlaybackRate(); new rate = ',
          this.playbackRate,
        );
        this._sa.notifyChangePlaybackRate(this.playbackRate);
        break;
      case 'BREAK_STARTED':
        this.onDemandAdsInfo.isOndemandAdPlaying = true;
        this.logAnalytics(
          TAG,
          ' >>>>> ',
          'isOndemandAdPlaying = ',
          this.onDemandAdsInfo.isOndemandAdPlaying,
        );
        break;
      case 'BREAK_ENDED':
        this.onDemandAdsInfo.isOndemandAdPlaying = false;
        this.logAnalytics(
          TAG,
          ' >>>>> ',
          'isOndemandAdPlaying = ',
          this.onDemandAdsInfo.isOndemandAdPlaying,
        );
        break;
      case 'BREAK_CLIP_STARTED': {
        const position = this._getPlaybackPosition();
        // Log notifyBufferStop() if bufferring occured
        this.logAnalytics(TAG, ' >>>>> ', 'isBuffering = ', this.isBuffering);
        if (this.isBuffering) {
          this.logAnalytics(TAG, ' >>>>> ', `startFromPosition(${position})`);
          this._sa.startFromPosition(position);
          this.logAnalytics(TAG, ' >>>>> ', 'notifyBufferStop()');
          this._sa.notifyBufferStop();
        }
        this._getOdAdsInfo();
        this.logAnalytics(TAG, ' >>>>> ', 'Load Ad metadata');
        this._setMetadata(this._getAdsMetadata(event, false));
        this.logAnalytics(TAG, ' >>>>> ', `startFromPosition(${position})`);
        this._sa.startFromPosition(position);
        this.logAnalytics(TAG, ' >>>>> ', 'notifyPlay()');
        this._sa.notifyPlay();
        break;
      }
      case 'BREAK_CLIP_ENDED':
        this.onPlaybackFinished();
        break;
      default:
        event.handleInSuper = true;
        break;
    }
    return event;
  }

  onCafShutdown() {
    this.logAnalytics(TAG, ' >>>>> ', 'onCafShutdown()');
    this.onPlaybackFinished();
    this.release();
  }

  _prepareAndSetContentInfo() {
    this.contentMetadata = this._getContentMetadata();
    this._setMetadata(this.contentMetadata);
  }

  onPlaybackFinished() {
    this.logAnalytics(TAG, ' >>>>> ', 'notifyEnd()');
    this._sa.notifyEnd();
  }

  _getPlaybackPosition() {
    let position = 0;
    if (this.onDemandAdsInfo.isOndemandAdPlaying) {
      position = Math.floor(this.playerManager.getBreakClipCurrentTimeSec()) * MILLIS_IN_SECOND;
    } else {
      position = Math.floor(this.playerManager.getCurrentTimeSec()) * MILLIS_IN_SECOND;
    }
    return position;
  }

  _playerStateChanged(event: Object) {
    this.logAnalytics(
      TAG,
      'MEDIA_STATUS  playerState = ',
      event.mediaStatus.playerState,
      '  progress = ',
      event.mediaStatus.currentTime,
      '  currentBreakTime = ',
      event.mediaStatus?.breakStatus?.currentBreakTime,
      '  currentBreakClipTime = ',
      event.mediaStatus?.breakStatus?.currentBreakClipTime,
      '  getCurrentTimeSec = ',
      this.playerManager.getCurrentTimeSec(),
    );
  }

  _configureAndStartComscore() {
    this.logAnalytics(TAG, '_configureAndStartComscore() +++');
    const comscorePublisherId = this.customAttributes?.comscore_partner_id || COMSCORE_PARTNER_ID;
    const publisherConfig = new ns_.configuration.PublisherConfiguration({
      publisherId: comscorePublisherId,
    });
    this.logAnalytics(TAG, 'comscorePublisherId = ', comscorePublisherId);

    ns_.configuration.addClient(publisherConfig);
    ns_.configuration.setApplicationName(this.appName || APP_NAME);
    this.logAnalytics(TAG, 'appName = ', this.appName || APP_NAME);
    ns_.configuration.setApplicationVersion(this.appVersion);
    this.logAnalytics(TAG, 'appVersion = ', this.appVersion);
    if (this.enableComscoreDebug) {
      ns_.configuration.enableImplementationValidationMode(); // for debug only!!!
    }
    ns_.start();
    this.logAnalytics(TAG, '_configureAndStartComscore() ---');
  }

  _getContentMetadata() {
    this.logAnalytics(TAG, '_getContentMetadata() +++');
    const cm = new ns_.StreamingAnalytics.ContentMetadata();
    const mediaType = this._getMediaType(this.sourceType);
    this.logAnalytics(TAG, '     MediaType = ', mediaType);
    cm.setMediaType(mediaType);
    const uniqueId = String(this._getUniqueId(this.sourceType));
    this.logAnalytics(TAG, '     UniqueId = ', uniqueId);
    cm.setUniqueId(uniqueId);
    this.logAnalytics(TAG, '     Length = ', this.contentLength * MILLIS_IN_SECOND);
    cm.setLength(this.contentLength * MILLIS_IN_SECOND);
    this.logAnalytics(TAG, '     DictionaryClassificationC3 = ', C3_VALUE);
    cm.setDictionaryClassificationC3(C3_VALUE);
    const networkName = isLiveLinearSourceType(this.sourceType)
      ? this.channelName || COMSCORE_NULL_STRING
      : this.networkName || COMSCORE_NULL_STRING;
    this.logAnalytics(TAG, '     DictionaryClassificationC4 = ', networkName);
    cm.setDictionaryClassificationC4(networkName);
    this.logAnalytics(TAG, '     DictionaryClassificationC6 = ', this.showName);
    cm.setDictionaryClassificationC6(this.showName); // Show title
    this.logAnalytics(TAG, '     StationTitle = ', networkName);
    cm.setStationTitle(networkName); // or same as C4 param!
    if (isLiveLinearSourceType(this.sourceType)) {
      this.logAnalytics(TAG, '     StationCode = ', this.channelNumber);
      cm.setStationCode(this.channelNumber);
    }
    this.logAnalytics(TAG, '     NetworkAffiliate = ', networkName);
    cm.setNetworkAffiliate(networkName); //  local TV station call sign
    this.logAnalytics(TAG, '     PublisherName = ', networkName);
    cm.setPublisherName(networkName);
    this.logAnalytics(TAG, '     ProgramTitle = ', this.showName);
    cm.setProgramTitle(this.showName);
    this.logAnalytics(TAG, '     ProgramId = ', this.contentId);
    cm.setProgramId(this.contentId); // This should not be confused with setUniqueId( String id ) which identifies an individual asset!
    if (!isLiveLinearSourceType(this.sourceType)) {
      const episodeTitle = isEmpty(this.episodeTitle) ? COMSCORE_NULL_STRING : this.episodeTitle;
      this.logAnalytics(TAG, '     EpisodeTitle = ', episodeTitle);
      cm.setEpisodeTitle(episodeTitle);
      const episodeId = isEmpty(this.episodeId) ? COMSCORE_NULL_STRING : this.episodeId;
      this.logAnalytics(TAG, '     EpisodeId = ', episodeId);
      cm.setEpisodeId(episodeId);
      const seasonNumber =
        this.seasonNumber === undefined
          ? COMSCORE_NULL_STRING
          : this.seasonNumber > 9
          ? this.seasonNumber
          : `0${this.seasonNumber}`;
      this.logAnalytics(TAG, '     SeasonNumber = ', seasonNumber);
      cm.setEpisodeSeasonNumber(seasonNumber);
      const episodeNumber =
        this.episodeNumber === undefined
          ? COMSCORE_NULL_STRING
          : this.episodeNumber > 99
          ? this.episodeNumber
          : `0${this.episodeNumber}`;
      this.logAnalytics(TAG, '     EpisodeNumber = ', episodeNumber);
      cm.setEpisodeNumber(episodeNumber);
    }
    this.logAnalytics(TAG, '     GenreName = ', this.contentGenresString);
    cm.setGenreName(this.contentGenresString); // Genre description. Multiple values can be provided as a comma-separated string.

    const adsAvailable = this._isAdsAvailable(this.sourceType);

    this.logAnalytics(TAG, '     TvAdvertisementLoad = ', adsAvailable);
    cm.carryTvAdvertisementLoad(adsAvailable);
    this.logAnalytics(TAG, '     classifyAsCompleteEpisode = ', this.isCompleteEpisode);
    cm.classifyAsCompleteEpisode(this.isCompleteEpisode); // false for episodes and true otherwise for full lenght content

    const { yearProd, monthProd, dayProd } = this.dateOfContentProduction;
    this.logAnalytics(TAG, '     DateOfProduction = ', `${yearProd}/${monthProd}/${dayProd}`);
    cm.setDateOfProduction(yearProd, monthProd, dayProd);
    const { hoursProd, minutesProd } = this.timeOfContentProduction;
    this.logAnalytics(TAG, '     TimeOfProduction = ', `${hoursProd} : ${minutesProd}`);
    cm.setTimeOfProduction(hoursProd, minutesProd);
    if (isLiveLinearSourceType(this.sourceType)) {
      const { year, month, day, hours, minutes } = this.dateAndTimeOfTvAiring;
      this.logAnalytics(TAG, '     DateOfTvAiring = ', `${year}/${month}/${day}`);
      cm.setDateOfTvAiring(year, month, day);
      this.logAnalytics(TAG, '     TimeOfTvAiring = ', `${hours} : ${minutes}`);
      cm.setTimeOfTvAiring(hours, minutes);
      this.logAnalytics(TAG, '     DateOfDigitalAiring = ', `${year}/${month}/${day}`);
      cm.setDateOfDigitalAiring(year, month, day);
      this.logAnalytics(TAG, '     TimeOfDigitalAiring = ', `${hours} : ${minutes}`);
      cm.setTimeOfDigitalAiring(hours, minutes);
    }
    const deliveryMode = this._getDeliveryMode(this.sourceType);
    this.logAnalytics(TAG, '     DeliveryMode = ', deliveryMode);
    cm.setDeliveryMode(deliveryMode);
    const deliveryComposition = this._getDeliveryComposition();
    this.logAnalytics(TAG, '     DeliveryComposition = ', deliveryComposition);
    cm.setDeliveryComposition(deliveryComposition);
    this.logAnalytics(TAG, '     DeliveryAdvertisementCapability = DYNAMIC_LOAD');
    cm.setDeliveryAdvertisementCapability(
      ns_.StreamingAnalytics.ContentMetadata.ContentDeliveryAdvertisementCapability.DYNAMIC_LOAD,
    );
    const mediaFormat = this._getMediaFormat();
    this.logAnalytics(TAG, '     MediaFormat = ', mediaFormat);
    cm.setMediaFormat(mediaFormat);
    const distributionModel = this._getDistributionModel();
    this.logAnalytics(TAG, '     DistributionModel = ', distributionModel);
    cm.setDistributionModel(distributionModel);
    const totalSegments = this._getTotalSegments();
    this.logAnalytics(TAG, '     TotalSegments = ', totalSegments);
    cm.setTotalSegments(totalSegments);
    this.logAnalytics(TAG, '     ClipUrl = ', this.castClipUrl);
    cm.setClipUrl(this.castClipUrl);
    if (this.contentWidth && this.contentHeight) {
      this.logAnalytics(TAG, '     VideoDimensions = ', this.contentWidth, 'x', this.contentHeight);
      cm.setVideoDimensions(this.contentWidth, this.contentHeight);
    }
    this.logAnalytics(TAG, '_getContentMetadata() ---', cm);

    return cm;
  }

  _getAdsMetadata(event: Object, isLiveLinearAd: boolean) {
    this.logAnalytics(TAG, '_getAdsMetadata() +++');
    const am = new ns_.StreamingAnalytics.AdvertisementMetadata();
    const currentLlAdInfo = this.getCurrentLiveLinearAdInfo(event.media?.contentUrl);
    const currentOdAdBreak = this.playerManager
      .getBreakManager()
      .getBreakClipById(event.breakClipId);
    const breakId = this.getBreakId(event);
    const adBreakType = this._getAdBreakType(breakId);
    this.logAnalytics(TAG, '     MediaType = ', adBreakType);
    am.setMediaType(adBreakType);
    const breakClipId = !isLiveLinearAd
      ? event.breakClipId
      : currentLlAdInfo
      ? currentLlAdInfo.id
      : COMSCORE_NULL_STRING;
    this.logAnalytics(TAG, '     UniqueId = ', breakClipId);
    am.setUniqueId(event.breakClipId);
    const adDuration = !isLiveLinearAd
      ? this.onDemandAdsInfo.breakClipDurationSec
      : currentLlAdInfo
      ? currentLlAdInfo.duration
      : 0;
    this.logAnalytics(TAG, '     Length = ', adDuration * MILLIS_IN_SECOND);
    am.setLength(adDuration * MILLIS_IN_SECOND);
    this.logAnalytics(TAG, '     DeliveryType = LOCAL');
    am.setDeliveryType(
      ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementDeliveryType.LOCAL,
    );
    this.logAnalytics(TAG, '     Owner = MULTIPLE');
    am.setOwner(ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementOwner.MULTIPLE);
    this.logAnalytics(TAG, '     Server = ', ADS_DELIVERY_SERVICE);
    am.setServer(ADS_DELIVERY_SERVICE);
    const adTitle = !isLiveLinearAd
      ? currentOdAdBreak.title
      : currentLlAdInfo
      ? currentLlAdInfo.title
      : COMSCORE_NULL_STRING;
    this.logAnalytics(TAG, '     Title = ', adTitle);
    am.setTitle(adTitle);
    const adClickThroughUrl = !isLiveLinearAd
      ? currentOdAdBreak.clickThroughUrl
      : COMSCORE_NULL_STRING;
    this.logAnalytics(TAG, '     CallToActionUrl = ', adClickThroughUrl);
    am.setCallToActionUrl(adClickThroughUrl);
    const adClipUrl = !isLiveLinearAd
      ? currentOdAdBreak.contentId
      : currentLlAdInfo
      ? currentLlAdInfo.adUrl
      : COMSCORE_NULL_STRING;
    this.logAnalytics(TAG, '     ClipUrl = ', adClipUrl);
    am.setClipUrl(adClipUrl);
    if (this.contentWidth && this.contentHeight) {
      am.setVideoDimensions(this.contentWidth, this.contentHeight);
    }
    this.logAnalytics(TAG, '_getAdsMetadata() ---', am);

    return am;
  }

  _getOdAdsInfo() {
    const breakManager = this.playerManager.getBreakManager();
    this.onDemandAdsInfo.breakClipCurrentTimeSec = breakManager.getBreakClipCurrentTimeSec();
    this.onDemandAdsInfo.breakClipDurationSec = Math.floor(breakManager.getBreakClipDurationSec());
    this.onDemandAdsInfo.breaks = breakManager.getBreaks();
    this.logAnalytics(TAG, 'this.onDemandAdsInfo = ', this.onDemandAdsInfo);
  }

  _isAdsAvailable(sourceType: string) {
    let result = false;
    if (isLiveLinearSourceType(sourceType)) {
      result = Array.isArray(this.liveLinearAdBreaks) && this.liveLinearAdBreaks.length > 0;
    } else {
      result = Array.isArray(this.odAdSlots) && this.odAdSlots.length > 0;
    }
    return result;
  }

  _loadLiveLinearAdMetadata(event: Object) {
    const position = this._getPlaybackPosition();
    this.logAnalytics(TAG, ' >>>>> ', 'Load Ad metadata');
    this.logAnalytics(TAG, ' >>>>> ', 'this.liveLinearAdSlotInfo = ', this.liveLinearAdSlotInfo);
    this._setMetadata(this._getAdsMetadata(event, true));
    this.logAnalytics(TAG, ' >>>>> ', `startFromPosition(${position})`);
    this._sa.startFromPosition(position);
    this.logAnalytics(TAG, ' >>>>> ', 'notifyPlay()');
    this._sa.notifyPlay();
  }

  _setMetadata(metaData: Object) {
    this._sa.setMetadata(metaData);
  }

  _getUniqueId(sourceType: string) {
    let id = this.contentId;
    if (this.isOndemandOrEstSourceType(sourceType)) {
      id = this.videoId;
    }
    return id;
  }

  _getMediaType(sourceType: string) {
    let mediaType = ns_.StreamingAnalytics.ContentMetadata.ContentType.OTHER;
    if (this.isOndemandOrEstSourceType(sourceType)) {
      if (this.contentLength > 10 * SECONDS_IN_MINUTE) {
        mediaType = ns_.StreamingAnalytics.ContentMetadata.ContentType.LONG_FORM_ON_DEMAND;
      } else {
        mediaType = ns_.StreamingAnalytics.ContentMetadata.ContentType.SHORT_FORM_ON_DEMAND;
      }
    } else if (isLiveLinearSourceType(sourceType)) {
      mediaType = ns_.StreamingAnalytics.ContentMetadata.ContentType.LIVE;
    }
    return mediaType;
  }

  _getDeliveryMode(sourceType: string) {
    let deliveryMode = ns_.StreamingAnalytics.ContentMetadata.ContentDeliveryMode.ON_DEMAND;
    if (isLiveLinearSourceType(sourceType)) {
      deliveryMode = ns_.StreamingAnalytics.ContentMetadata.ContentDeliveryMode.LINEAR;
    }
    return deliveryMode;
  }

  _getDeliveryComposition() {
    return ns_.StreamingAnalytics.ContentMetadata.ContentDeliveryComposition.CLEAN;
  }

  _getMediaFormat() {
    return this.isCompleteEpisode
      ? ns_.StreamingAnalytics.ContentMetadata.ContentMediaFormat.FULL_CONTENT_MOVIE
      : ns_.StreamingAnalytics.ContentMetadata.ContentMediaFormat.FULL_CONTENT_EPISODE;
  }

  _getAdBreakType(adBreakId?: string) {
    let adBreakType =
      ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_MID_ROLL;
    const currentAdBreakType = this.getAdBreakType(adBreakId);
    if (this.isOndemandOrEstSourceType(this.sourceType)) {
      if (currentAdBreakType === AD_BREAK_TYPE_PREROLL) {
        adBreakType =
          ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_PRE_ROLL;
      } else if (currentAdBreakType === AD_BREAK_TYPE_POSTROLL) {
        adBreakType =
          ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_POST_ROLL;
      } else {
        adBreakType =
          ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.ON_DEMAND_MID_ROLL;
      }
    } else if (isLiveLinearSourceType(this.sourceType)) {
      adBreakType = ns_.StreamingAnalytics.AdvertisementMetadata.AdvertisementType.LIVE;
    }
    return adBreakType;
  }

  _getTotalSegments() {
    const breakClips = isLiveLinearSourceType(this.sourceType)
      ? this.liveLinearAdBreaks
      : this.playerManager.getBreakManager().getBreakClips();
    let result = 1;
    if (Array.isArray(breakClips)) {
      result = breakClips.reduce((sum, current) => {
        const { type = '', title = '' } = current;
        const isMidroll = isLiveLinearSourceType(this.sourceType)
          ? type.toUpperCase().includes(AD_BREAK_TYPE_MIDROLL)
          : title.includes(AD_BREAK_TYPE_MIDROLL);
        if (isMidroll) {
          sum += 1;
        }
        return sum;
      }, 1);
    }
    return result;
  }

  _getDistributionModel() {
    return isLiveLinearSourceType(this.sourceType)
      ? ns_.StreamingAnalytics.ContentMetadata.ContentDistributionModel.TV_AND_ONLINE
      : ns_.StreamingAnalytics.ContentMetadata.ContentDistributionModel.EXCLUSIVELY_ONLINE;
  }

  release() {
    this.isInitialized = false;
    this._sa = {};
    this.onDemandAdsInfo = {};
    this.contentMetadata = {};
  }

  _isOdOrLlAdPlaying() {
    return this.onDemandAdsInfo.isOndemandAdPlaying || this.isLlAdPlaying;
  }
}

export default ComscoreCastAnalytics;
